import { inject } from '@angular/core'
import {
    Router,
    CanActivateFn,
    ActivatedRouteSnapshot,
    RouterStateSnapshot,
} from '@angular/router'
import { toast } from '@spartan-ng/brain/sonner'
import { AuthStateService } from '../auth/auth-state.service'

export interface AccessGuardOptions {
    requireAuthenticated?: boolean
    requireGroups?: string[]
    requirePermissions?: string[]
    requireOwner?: boolean
    options?: {
        redirectTo?: string[]
        showError?: boolean
    }
}

export function accessGuard(config: AccessGuardOptions = {}): CanActivateFn {
    return (_route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
        const authStateService = inject(AuthStateService)
        const router = inject(Router)

        const {
            requireAuthenticated = true,
            requireGroups = [],
            requirePermissions = [],
            requireOwner,
            options = {},
        } = config

        const { redirectTo = ['/'], showError = false } = options

        // 1. Check Authentication
        if (requireAuthenticated && !authStateService.isLoggedIn()) {
            if (showError) {
                toast.error('You must be logged in to access this page')
            }
            return router.createUrlTree(['/login'], {
                queryParams: { returnUrl: state.url },
            })
        }

        // 2. Check Group Access (only if groups are specified)
        if (requireGroups.length > 0) {
            const userGroupType = authStateService.getGroupType()
            const hasGroupAccess = requireGroups.some(
                (type) => type === userGroupType,
            )

            if (!hasGroupAccess) {
                if (showError) {
                    toast.error('You do not have permission to view this page.')
                }
                return router.createUrlTree(redirectTo)
            }
        }

        // 3. Check Permissions (only if permissions are specified)
        if (requirePermissions.length > 0) {
            const hasPermission = requirePermissions.some((perm) =>
                authStateService.hasPermission(perm),
            )

            if (!hasPermission) {
                if (showError) {
                    toast.error('You do not have permission to view this page.')
                }
                return router.createUrlTree(redirectTo)
            }
        }

        // 4. Check Ownership (if requireOwner is specified)
        if (requireOwner !== undefined) {
            const isOwner = authStateService.isOwner()

            if (requireOwner === true && !isOwner) {
                if (showError) {
                    toast.error(
                        'You do not have administrative access to this page.',
                    )
                }
                const redirectTarget = options.redirectTo ?? ['/client']
                return router.createUrlTree(redirectTarget)
            }

            if (requireOwner === false && isOwner) {
                if (showError) {
                    toast.error(
                        'Administrators cannot access member dashboard.',
                    )
                }
                const redirectTarget = options.redirectTo ?? ['/client-admin']
                return router.createUrlTree(redirectTarget)
            }
        }

        return true
    }
}

export function clientAdminGuard(
    config: Partial<AccessGuardOptions> = {},
): CanActivateFn {
    return accessGuard({
        requireAuthenticated: true,
        requireGroups: ['client'],
        requireOwner: true,
        options: {
            redirectTo: ['/client'],
            showError: false,
            ...config.options,
        },
        ...config,
    })
}

export function clientMemberGuard(
    config: Partial<AccessGuardOptions> = {},
): CanActivateFn {
    return accessGuard({
        requireAuthenticated: true,
        requireGroups: ['client'],
        requireOwner: false,
        options: {
            redirectTo: ['/client-admin'],
            showError: false,
            ...config.options,
        },
        ...config,
    })
}
